/**
 * MissingInfoStatusBadge — badge cohérent pour les 5 statuts Missing Information.
 *
 * Mapping enum `app.missing_information_status` → tons brand alignés :
 *   - open         → amber (à résoudre, attire l'attention)
 *   - in_progress  → brand-blue (travail actif en cours)
 *   - fulfilled    → emerald (résolu, succès)
 *   - waived       → slate (clos, sans objet)
 *   - superseded   → slate (clos, remplacé)
 *
 * Pattern parité `QuoteRequestStatusBadge` 9.L.6 + `ProjectStatusBadge` 9.G.2.
 * Emplacement `_parts/projects/` Phase 1 : consommé par Project + Submission
 * sections (cf. 9.L.12 décision Q4 PO — pas d'over-engineering global).
 */

import { useTranslation } from "react-i18next";

import type { MissingInfoStatus } from "@kollectivia/shared";

type Tone = "slate" | "amber" | "brand" | "emerald";

const TONE: Record<Tone, { bg: string; text: string; dot: string }> = {
  slate: { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  brand: { bg: "bg-brand-50", text: "text-brand-700", dot: "bg-brand-500" },
  emerald: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
  },
};

function toneFor(status: MissingInfoStatus): Tone {
  switch (status) {
    case "open":
      return "amber";
    case "in_progress":
      return "brand";
    case "fulfilled":
      return "emerald";
    case "waived":
      return "slate";
    case "superseded":
      return "slate";
    default:
      return "slate";
  }
}

type Props = {
  status: MissingInfoStatus;
};

export function MissingInfoStatusBadge({ status }: Props) {
  const { t } = useTranslation();
  const tone = TONE[toneFor(status)];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ${tone.bg} px-2.5 py-0.5 text-xs font-medium ${tone.text}`}
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${tone.dot}`}
      />
      {t(`missing_information.statuses.${status}`)}
    </span>
  );
}
